import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector, useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import {
  apiRequest,
  apiSuccess,
  apiFetchError,
} from "../../redux/api/apiActions";
import { showAlert } from "../../redux/alert/alertActions";
import SubmitLoading from "../../components/common/SubmitLoading";
import Breadcrumbs from "../../components/private/Breadcrumbs";
import PatientSelection from "../../components/private/visits/PatientSelection";
import { useUser } from "../../authentication/authentication";
import api from "../../api";
import VisitSelection from "../../components/private/payments/addPayment/VisitSelection";
import Review from "../../components/private/payments/addPayment/Review";
import PaymentDetails from "../../components/private/payments/addPayment/PaymentDetails";
import Txt from "../../components/Text";
import { arClass } from "../../utils/useArClass";
import textTranslator from "../../utils/useTextTranslator";

/*
  CreatePayment component
  Steps:
    0 - PatientSelection (reuse existing component)
    1 - VisitSelection  (fetch visits for selected patient / user)
    2 - PaymentDetails  (amount, method, discount, notes)
    3 - ReviewComplete  (summary + submit)
*/

const Text = ({ children, className, page = "PAYMENTS" }) => {
  return (
    <Txt page={page} className={className} keyName={page}>
      {children}
    </Txt>
  );
};

const createPaymentKey = "create-payment-" + uuidv4().split("-")[0];

const sidebarVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

export default function CreatePayment() {
  const dispatch = useDispatch();
  const darkMode = useSelector((s) => s.settings.darkMode);
  const { requests } = useSelector((s) => s.api);
  const isCreating = requests[createPaymentKey]?.loading;
  const [step, setStep] = useState(0);

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [payment, setPayment] = useState({
    amount: "",
    method: "cash",
    discountPercent: "",
    notes: "",
    paymentStatus: "pending",
  });

  const user = useUser();

  const steps = [
    textTranslator("Patient", "PAYMENTS"),
    textTranslator("Visit", "PAYMENTS"),
    textTranslator("Payment", "PAYMENTS"),
    textTranslator("Review", "PAYMENTS")
  ];
  
  // Color scheme (unchanged)
  const colors = {
    background: darkMode ? "bg-gray-900" : "bg-gray-50",
    card: darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200",
    cardHover: darkMode ? "hover:border-gray-600" : "hover:border-gray-400",
    input: darkMode
      ? "bg-gray-700 border-gray-600 placeholder-gray-500"
      : "bg-white border-gray-300 placeholder-gray-400",
    subBg: darkMode ? "bg-gray-700" : "bg-gray-100",
    text: {
      primary: darkMode ? "text-white" : "text-gray-800",
      secondary: darkMode ? "text-gray-300" : "text-gray-600",
      muted: darkMode ? "text-gray-400" : "text-gray-500",
    },
    button: {
      primary: darkMode
        ? "bg-blue-600 text-white hover:bg-blue-500"
        : "bg-blue-500 text-white hover:bg-blue-600",
      secondary: darkMode
        ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300",
      danger: "bg-gradient-to-br from-red-500 to-pink-600 text-white",
    },
  };

  const next = () => {
    if (step === 0 && !selectedPatient) return;
    if (step === 1 && !selectedVisit) return;
    if (step === 2 && (!payment.amount || Number(payment.amount) <= 0)) return;
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const handleSubmitPayment = async () => {
    if (!selectedVisit || !selectedPatient) {
      dispatch(
        showAlert({
          type: "error",
          title: textTranslator("Missing", "PAYMENTS"),
          message: textTranslator("Select patient and visit.", "PAYMENTS"),
        })
      );
      return;
    }
    try {
      dispatch(apiRequest("POST", createPaymentKey));
      const payload = {
        visitId: selectedVisit.id,
        patientId: selectedPatient.id,
        amount: Number(payment.amount),
        amountAfterDiscount:
          Number(payment.amount) *
          (1 - (Number(payment.discountPercent) || 0) / 100),
        method: payment.method,
        discountPercent: payment.discountPercent
          ? Number(payment.discountPercent)
          : 0,
        notes: payment.notes || "",
        paymentStatus: payment.paymentStatus || "pending",
      };
      // endpoint may vary by backend; using generic 'payments' POST
      await api.post(`payments/create-payment/${user.id}`, payload);
      dispatch(apiSuccess("POST", createPaymentKey, textTranslator("Payment created", "PAYMENTS")));
      dispatch(
        showAlert({
          type: "success",
          title: textTranslator("Success", "PAYMENTS"),
          message: textTranslator("Payment recorded.", "PAYMENTS"),
        })
      );
      // reset
      setSelectedPatient(null);
      setSelectedVisit(null);
      setPayment({
        amount: "",
        method: "cash",
        discountPercent: "",
        notes: "",
      });
      setStep(0);
    } catch (err) {
      // Handle error silently
      const msg =
        err?.response?.data?.error || err.message || textTranslator("Failed to create payment", "PAYMENTS");
      dispatch(apiFetchError("POST", createPaymentKey, msg));
      dispatch(showAlert({ type: "error", title: textTranslator("Error", "PAYMENTS"), message: msg }));
    }
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <PatientSelection
            darkMode={darkMode}
            onPatientSelect={(p) => {
              setSelectedPatient(p);
              setSelectedVisit(null);
              if (p) setStep(1);
            }}
            selectedPatient={selectedPatient}
            colors={colors}
          />
        );
      case 1:
        return (
          <VisitSelection
            selectedPatient={selectedPatient}
            onSelect={(v) => {
              setSelectedVisit(v);
              if (v && !v.payment) setStep(2);
            }}
            darkMode={darkMode}
            colors={colors}
            selectedVisit={selectedVisit}
          />
        );
      case 2:
        return (
          <PaymentDetails
            payment={payment}
            setPayment={setPayment}
            colors={colors}
          />
        );
      case 3:
        return (
          <Review
            payment={payment}
            selectedPatient={selectedPatient}
            selectedVisit={selectedVisit}
            onEdite={() => setStep(1)}
            onSubmit={handleSubmitPayment}
          />
        );
      default:
        return null;
    }
  };

  const handleDisableStepClick = (targetStep, currentStep) => {
    // always allow navigating to the current step or any previous step
    if (targetStep <= currentStep) return false;

    // Prevent skipping ahead unless required data for that step is present
    switch (targetStep) {
      case 1:
        // Visit step requires a selected patient
        return !selectedPatient;
      case 2:
        // Payment details step requires a selected patient and a selected visit
        return !(selectedPatient && selectedVisit && !selectedVisit.payment);
      case 3:
        // Review step requires patient, visit and a valid payment amount (> 0)
        return !(
          selectedPatient &&
          selectedVisit &&
          payment &&
          Number(payment.amount) > 0
        );
      default:
        return false;
    }
  };

  const handleDisableNextClick = (currentStep) => {
    let disabled = false;
    let title = textTranslator("Next Step", "PAYMENTS");
    switch (currentStep) {
      case 0:
        if (!selectedPatient) {
          disabled = true;
          title = textTranslator("Select a patient to proceed", "PAYMENTS");
        }
        break;
      case 1:
        if (!selectedVisit || selectedVisit.payment) {
          disabled = true;
          if (selectedVisit && selectedVisit.payment) {
            title = textTranslator("Selected visit already has a payment", "PAYMENTS");
          } else {
            title = textTranslator("Select a visit to proceed", "PAYMENTS");
          }
        }
        break;
      case 2:
        if (!payment || Number(payment.amount) <= 0) {
          disabled = true;
          title = textTranslator("Enter a valid payment amount to proceed", "PAYMENTS");
        }
        break;
      default:
        break;
    }
    return { disabled, title };
  };

  return (
    <div className={arClass("")}>
      <Breadcrumbs
        links={[{ label: textTranslator("Visits", "PAYMENTS"), to: "/visits" }, { label: textTranslator("Create Payment", "PAYMENTS") }]}
      />
      {isCreating && (
        <SubmitLoading colors={{}} message={textTranslator("Recording payment...", "PAYMENTS")} />
      )}
      <div className={arClass("p-6 transition-colors duration-300 max-sm:p-4")}>
        <div
          className={arClass(`min-h-[80vh] rounded-3xl shadow-2xl overflow-hidden grid grid-cols-12 max-sm:flex max-sm:flex-col ${colors.card} border`)}
        >
          {/* Enhanced Sidebar */}
          <motion.aside
            className={arClass(`col-span-2 p-6 max-sm:p-2 transition-colors duration-300  ${colors.subBg}`)}
            initial="hidden"
            animate="visible"
            variants={sidebarVariants}
          >
            <motion.h2
              className={arClass("text-2xl font-bold mb-8 bg-gradient-to-r max-sm:text-center max-sm:text-lg max-sm:mb-4")}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Text>Create Payment</Text>
            </motion.h2>
            <nav className={arClass("space-y-6 max-sm:flex  max-sm:space-y-0 max-sm:justify-between")}>
              {steps.map((label, i) => (
                <motion.button
                  key={label}
                  className={arClass("flex items-center disabled:cursor-not-allowed max-sm:flex-col max-sm:space-y-2 max-sm:space-x-0")}
                  whileHover={{ x: 4 }}
                  transition={{ duration: 0.2 }}
                  onClick={() => setStep(i)}
                  disabled={handleDisableStepClick(i, step)}
                  title={`${
                    handleDisableStepClick(i, step)
                      ? textTranslator("Complete previous steps first", "PAYMENTS")
                      : `${textTranslator("Go to", "PAYMENTS")} ${label} ${textTranslator("step", "PAYMENTS")}`
                  }`}
                >
                  <div
                    className={arClass(`w-10 h-10 rounded-lg flex items-center justify-center mr-3 transition-all duration-300 max-sm:w-8 max-sm:h-8 max-sm:text-xs max-sm:rounded-md ${
                      i === step
                        ? colors.button.primary
                        : `${
                            darkMode
                              ? "bg-gray-800 text-gray-300"
                              : "bg-gray-200 text-gray-600"
                          }`
                    }`)}
                  >
                    {i + 1}
                  </div>
                  <div
                    className={arClass(`text-sm font-medium max-sm:text-xs ${
                      i === step ? colors.text.primary : colors.text.secondary
                    }`)}
                  >
                    {label}
                  </div>
                </motion.button>
              ))}
            </nav>
          </motion.aside>

          <main className={arClass("col-span-10 p-6 max-sm:p-4")}>
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                className={arClass("h-full flex flex-col")}
              >
                <div className={arClass("flex items-center justify-between mb-8")}>
                  <motion.h3
                    className={arClass("text-3xl font-bold max-sm:text-sm")}
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    {steps[step]}
                  </motion.h3>
                  <motion.div
                    className={arClass(`text-sm px-4 py-2 rounded-full max-sm:text-xs ${
                      darkMode
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-200 text-gray-600"
                    }`)}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    <Text>Step</Text> {step + 1} <Text>of</Text> {steps.length}
                  </motion.div>
                </div>
                <div className={arClass("flex-1")}>{renderStep()}</div>

                {/* Footer Controls */}
                <div className={arClass("mt-8 flex items-center justify-between pt-6 border-gray-200")}>
                  <div className="flex gap-3">
                    <motion.button
                      disabled={step === 0}
                      onClick={back}
                      className={arClass(`px-6 py-3 rounded-xl font-semibold transition-all duration-200 max-sm:text-xs max-sm:rounded-md max-sm:py-2 ${
                        step === 0
                          ? `${
                              darkMode
                                ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`
                          : darkMode
                          ? "bg-gray-700 text-gray-200 hover:bg-gray-600"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`)}
                      whileHover={step !== 0 ? { scale: 1.02, y: -1 } : {}}
                      whileTap={step !== 0 ? { scale: 0.98, y: 0 } : {}}
                    >
                      <Text>Back</Text>
                    </motion.button>

                    {step !== steps.length - 1 && (
                      <motion.button
                        onClick={next}
                        disabled={handleDisableNextClick(step).disabled}
                        className={arClass(`px-6 py-3 rounded-xl font-semibold transition-all duration-200 max-sm:text-xs max-sm:rounded-md max-sm:py-2 ${
                          handleDisableNextClick(step).disabled
                            ? darkMode
                              ? "bg-gray-700 text-gray-500 cursor-not-allowed"
                              : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : colors.button.primary
                        }`)}
                        whileHover={{ scale: 1.02, y: -1 }}
                        whileTap={{ scale: 0.98, y: 0 }}
                      >
                        <Text>Next</Text>
                      </motion.button>
                    )}

                    {step === steps.length - 1 && (
                      <motion.button
                        onClick={handleSubmitPayment}
                        className={arClass("px-8 py-3 rounded-xl font-semibold bg-green-600 text-white shadow-lg hover:shadow-md transition-all duration-200 max-sm:text-xs max-sm:rounded-md max-sm:px-6 max-sm:py-2")}
                        whileHover={{ scale: 1.02, y: -1 }}
                        whileTap={{ scale: 0.98, y: 0 }}
                      >
                        <Text>Complete Payment</Text>
                      </motion.button>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
}